import React, { memo, useCallback, useEffect, useState } from 'react';
import { defineMessages, injectIntl } from 'react-intl';
import { colors } from '@lifesize/ux-assets';
import { logger } from 'utils/logger';
import Tooltip from '@material-ui/core/Tooltip';
import { ReactComponent as ShareScreenIcon } from '@lifesize/ux-assets/vectors/svgIcons/share-screen.svg';
import { constants as c, hooks, media, signaling } from '@lifesize/clients.sdk';
import { useMedia } from '@lifesize/clients.sdk/dist/signaling/hooks';
import { useDispatch } from 'react-redux';
import actions from 'actions/PresentationActions';
import { Fab, makeStyles } from '@material-ui/core';
import { FLAG_PRESENTATION_RESTRICTION } from 'constants/FeatureFlags';
import useFeatureFlag from 'hooks/useFeatureFlag';
import _memoize from 'lodash/memoize';
import { setPillNotificationWithTimeout } from 'actions/Notification';

export interface PresentationButtonProps {
  intl: { formatMessage: Function };
  error?: string;
  compactButton?: boolean;
}

const messages = defineMessages({
  shareScreen: {
    id: 'components.incall.controls.screenShare.tooltip.share',
    description: 'Default share screen tooltip',
    defaultMessage: 'Share your screen'
  },
  stopShareScreen: {
    id: 'components.incall.controls.screenShare.tooltip.notShare',
    defaultMessage: 'Stop sharing your screen'
  },
  currentPresenter: {
    id: 'components.incall.controls.screenShare.tooltip.presenter',
    description: 'Name of current presenter',
    defaultMessage: '{name} is presenting'
  },
  presentationRestricted: {
    id: 'components.incall.controls.screenShare.tooltip.restricted',
    description: 'presentation restricted tooltip',
    defaultMessage: 'Only moderators, lecturers, and owners can share in this call'
  }
});

export const GALAXY_CONFERENCE_TYPE_VMR = 'VMR'; // TODO - taken from Nucleus

export const PresentationButton = (props: PresentationButtonProps) => {
  const { intl, error, compactButton } = props;

  const dispatch = useDispatch();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [mouseOver, setMouseOver] = useState(false);
  const currentUser = hooks.useCurrentUserParticipant() || {};
  const canPresent = currentUser?.canPresent || false;
  const mediaState = useMedia();
  const isModerator = hooks.useIsCurrentUserModerator();
  const conferenceInfo = signaling.selectors.conferenceInfo() || {};
  const { ownerUuid, lecturerUuid } = conferenceInfo;
  const presenter = hooks.usePresentingParticipant() || {};
  const isOwner = ownerUuid === presenter.uuid || ownerUuid === currentUser.uuid;
  const presentationRestriction: string = hooks.usePresentationRestriction() || '';
  const isConnected = hooks.useCallConnected();
  const isRemotePresentationExists = mediaState.remotePresentationStream && isConnected;
  const presentationRestrictionFlagEnabled = useFeatureFlag(FLAG_PRESENTATION_RESTRICTION);
  const isLecturer = lecturerUuid === presenter.uuid || currentUser.uuid === lecturerUuid;
  const isPresenting = !!mediaState.localPresentationStream;
  const isPresentationRestricted = presentationRestriction === c.PresentationRestriction.restrictedShare;
  const isDisabled = !isConnected || !!isRemotePresentationExists || !canPresent;
  const classes = useStyles({ ...getStyleOverride(isPresenting) });
  const tooltipClasses = useStylesTooltip();

  const showPresentationRestrictionTooltip = useCallback(() => {
    return conferenceInfo?.type === GALAXY_CONFERENCE_TYPE_VMR && !canPresent && !isRemotePresentationExists;
  }, [conferenceInfo, canPresent, isRemotePresentationExists]);

  const inactivePresentationTooltip = useCallback(() => {
    // check for presentationRestrictionEnabled before setting tooltip.
    if (showPresentationRestrictionTooltip()) {
      return intl.formatMessage(messages.presentationRestricted);
    } else if (isPresenting) {
      return intl.formatMessage(messages.stopShareScreen);
    } else if (isRemotePresentationExists && presenter?.name) {
      return intl.formatMessage(messages.currentPresenter, { name: presenter.name });
    } else {
      return intl.formatMessage(messages.shareScreen);
    }
  }, [intl, presenter, showPresentationRestrictionTooltip, isPresenting, isRemotePresentationExists]);

  const onStopPresentationClick = useCallback(() => {
    media.stopPresentation();
  }, []);

  const onShowPresentationSelection = useCallback(() => {
    dispatch(actions.showPresentationSelection());
  }, [dispatch]);

  useEffect(() => {
    if (!!errorMessage && !!error) {
      dispatch(setPillNotificationWithTimeout(errorMessage));
      setErrorMessage(undefined);
    }
  }, [dispatch, error, errorMessage, setErrorMessage]);

  useEffect(() => {
    if (!!error) {
      setErrorMessage(error);
    }
  }, [error, setErrorMessage]);

  useEffect(() => {
    if (
      presentationRestrictionFlagEnabled &&
      isPresenting &&
      !isModerator &&
      !isOwner &&
      !isLecturer &&
      isPresentationRestricted
    ) {
      try {
        onStopPresentationClick();
      } catch (e) {
        logger.error(`Error occurred while stopping presentation ${JSON.stringify(e)}`);
      }
    }
  }, [
    isLecturer,
    isModerator,
    isOwner,
    isPresentationRestricted,
    isPresenting,
    mediaState.presentationToken,
    onStopPresentationClick,
    presentationRestrictionFlagEnabled
  ]);

  useEffect(() => {
    if (mediaState.presentationToken === c.PRESENTATION_TOKEN_STATE_DENIED) {
      try {
        logger.warn('stopping presentation because presentation token is denied');
        onStopPresentationClick();
      } catch (e) {
        logger.error(`Error occurred while stopping presentation after${JSON.stringify(e)}`);
      }
    }
  }, [mediaState.presentationToken, onStopPresentationClick]);

  const ShareIcon = (
    <ShareScreenIcon
      onClick={isPresenting ? onStopPresentationClick : onShowPresentationSelection}
      data-testid="share-screen-icon"
      key="share-screen-icon"
    />
  );

  if (compactButton) return ShareIcon;
  return (
    <Tooltip title={inactivePresentationTooltip()} placement="top" arrow={true} classes={tooltipClasses}>
      <span
        onMouseOver={() => setMouseOver(true)}
        onMouseOut={() => setMouseOver(false)}
        data-testid={isDisabled ? 'disabled-presentation-btn' : 'enabled-presentation-btn'}
      >
        <Fab
          classes={classes}
          style={isDisabled && isRemotePresentationExists && mouseOver ? { borderColor: colors.white } : {}}
          data-testid="presentation-btn"
          disabled={isDisabled}
          size="medium"
        >
          {ShareIcon}
        </Fab>
      </span>
    </Tooltip>
  );
};

/** Style selectors that can be dynamically modified */
interface PresentationButtonStyleProps {
  backgroundColor: string;
  borderColor: string;
  opacity: number;
}

const getStyleOverride = _memoize(
  (isPresenting: boolean): PresentationButtonStyleProps => {
    return {
      backgroundColor: isPresenting ? colors.agileGreen : 'transparent',
      borderColor: isPresenting ? colors.agileGreen : colors.white,
      opacity: isPresenting ? 0.85 : 0.6 // Old opacity was 0.80
    };
  }
);

const useStyles = makeStyles(() => ({
  root: {
    backgroundColor: (btnStyle: PresentationButtonStyleProps) => btnStyle.backgroundColor,
    border: (btnStyle: PresentationButtonStyleProps) => `2px solid ${btnStyle.borderColor}`,
    boxShadow: 'none',
    color: colors.white,
    margin: 0,
    opacity: (btnStyle: PresentationButtonStyleProps) => btnStyle.opacity,
    '&:hover': {
      backgroundColor: (currentStyle: PresentationButtonStyleProps) => currentStyle.backgroundColor,
      borderColor: (currentStyle: PresentationButtonStyleProps) => currentStyle.borderColor,
      color: colors.white,
      opacity: 1.0
    },
    '& svg': {
      height: 40,
      width: 'auto'
    }
  },
  disabled: {
    color: `${colors.sentinel} !important`,
    borderColor: `${colors.sentinel} !important`,
    backgroundColor: `${colors.squidInk} !important`,
    opacity: `1 !important`
  }
}));

const useStylesTooltip = makeStyles(() => ({
  popper: {
    textAlign: 'center'
  }
}));

export default memo(injectIntl(PresentationButton));
